<template>
  <div :class="commentClasses">
    <!-- Main Comment -->
    <div class="flex items-start space-x-3">
      <!-- Avatar -->
      <img
        v-if="commentAuthorImageUrl"
        :src="commentAuthorImageUrl"
        :alt="comment.author.username"
        class="w-8 h-8 rounded-full object-cover flex-shrink-0"
      />
      <div
        v-else
        class="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0"
      >
        <span class="text-gray-600 font-medium text-sm">
          {{ comment.author.username.charAt(0).toUpperCase() }}
        </span>
      </div>

      <!-- Comment Content -->
      <div class="flex-1 min-w-0">
        <!-- Header with author and actions -->
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2"
        >
          <div class="flex items-center space-x-2 flex-wrap">
            <span class="font-medium text-sm text-gray-900">
              {{ comment.author.username }}
            </span>
            <span class="text-xs text-gray-500 whitespace-nowrap">
              {{ formatDate(comment.created_at) }}
            </span>

            <!-- Reply indicator for nested replies -->
            <span v-if="depth > 0" class="text-xs text-primary-600 font-medium">
              respondeu
            </span>
          </div>

          <!-- Action buttons -->
          <div class="flex items-center space-x-2 flex-shrink-0">
            <!-- Like Button -->
            <button
              v-if="authStore.isAuthenticated"
              @click="handleLike"
              :disabled="isUserOwnComment"
              class="flex items-center space-x-1 text-gray-500 hover:text-red-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
            >
              <HeartIcon
                class="w-3 h-3 transition-all duration-300"
                :class="[
                  {
                    'fill-current text-red-500':
                      comment.userInteractions?.liked,
                  },
                ]"
              />
              <span class="font-medium">{{ comment.stats?.likes || 0 }}</span>
            </button>

            <!-- Edit Button -->
            <button
              v-if="authStore.isAuthenticated && isUserOwnComment"
              @click="toggleEditForm"
              class="flex items-center space-x-1 text-gray-500 hover:text-blue-600 transition-colors duration-200 text-xs"
            >
              <svg
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                ></path>
              </svg>
              <span class="font-medium">Editar</span>
            </button>

            <!-- Reply Button -->
            <button
              v-if="authStore.isAuthenticated && depth < 1"
              @click="toggleReplyForm"
              class="flex items-center space-x-1 text-gray-500 hover:text-primary-600 transition-colors duration-200 text-xs"
            >
              <svg
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                ></path>
              </svg>
              <span class="font-medium">Responder</span>
            </button>
          </div>
        </div>

        <!-- Comment text / Edit form toggle -->
        <div v-if="!showEditForm" class="text-gray-700 leading-relaxed mb-3">
          {{ comment.content }}
        </div>

        <!-- Edit Form -->
        <div v-if="showEditForm" class="mb-3">
          <form @submit.prevent="submitEdit" class="space-y-3">
            <textarea
              v-model="editContent"
              :placeholder="'Editar comentário...'"
              class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all text-sm"
              rows="3"
              maxlength="200"
              required
            ></textarea>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">
                {{ editContent.length }}/200 caracteres
              </span>
              <div class="flex space-x-2">
                <button
                  type="button"
                  @click="toggleEditForm"
                  class="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  :disabled="!editContent.trim() || submittingEdit"
                  class="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                >
                  {{ submittingEdit ? "Salvando..." : "Salvar" }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Reply count indicator -->
        <div v-if="comment.replies && comment.replies.length > 0" class="mb-3">
          <button
            @click="toggleReplies"
            class="flex items-center space-x-2 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
          >
            <svg
              class="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
            <span
              >{{ showReplies ? "Ocultar" : "Ver" }}
              {{ comment.replies.length }}
              {{
                comment.replies.length === 1 ? "resposta" : "respostas"
              }}</span
            >
          </button>
        </div>

        <!-- Reply Form -->
        <div v-if="showReplyForm" class="mb-4">
          <form
            @submit.prevent="submitReply"
            class="space-y-3 bg-gray-50 rounded-lg p-4"
          >
            <textarea
              v-model="replyContent"
              :placeholder="`Responder a @${comment.author.username}...`"
              class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none transition-all text-sm"
              rows="3"
              maxlength="200"
              required
            ></textarea>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">
                {{ replyContent.length }}/200 caracteres
              </span>
              <div class="flex space-x-2">
                <button
                  type="button"
                  @click="toggleReplyForm"
                  class="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  :disabled="!replyContent.trim() || submittingReply"
                  class="px-3 py-1.5 text-xs bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                >
                  {{ submittingReply ? "Enviando..." : "Responder" }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Replies -->
        <div v-if="showReplies" class="space-y-4 mt-4">
          <CommentThread
            v-for="reply in comment.replies"
            :key="reply.id"
            :comment="reply"
            :post-id="postId"
            :depth="depth + 1"
            @reply="$emit('reply', $event)"
            @edit="$emit('edit', $event)"
            @like="$emit('like', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useAuthStore } from "@/store/auth";
import { HeartIcon } from "lucide-vue-next";

export default {
  name: "CommentThread",
  components: {
    HeartIcon,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    postId: {
      type: [String, Number],
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
  },

  emits: ["reply", "like", "edit"],
  setup(props, { emit }) {
    const authStore = useAuthStore();
    const showReplyForm = ref(false);

    // Computed para URL completa da imagem do autor do comentário
    const commentAuthorImageUrl = computed(() => {
      const rawUrl = props.comment?.author?.profile_image_url;
      if (!rawUrl) return null;

      // Se for base64, valida antes de usar
      if (rawUrl.startsWith("data:image/")) {
        try {
          // Verifica se o base64 está completo (mínimo 100 caracteres após o cabeçalho)
          const base64Data = rawUrl.split(",")[1];
          if (!base64Data || base64Data.length < 100) {
            console.warn("⚠️ [CommentThread] Base64 incompleto ou corrompido");
            return null;
          }
          return rawUrl;
        } catch (error) {
          console.error("❌ [CommentThread] Erro ao validar base64:", error);
          return null;
        }
      }

      // Se já for uma URL completa (https/http), usa direto
      if (rawUrl.startsWith("http")) {
        return rawUrl;
      }

      // Se for uma URL relativa (/uploads/...), constrói com a base da API
      const apiBase =
        process.env.VUE_APP_API_URL || "http://localhost:3000/api";
      const baseUrl = apiBase.replace("/api", "");
      const fullUrl = rawUrl.startsWith("/")
        ? baseUrl + rawUrl
        : baseUrl + "/" + rawUrl;
      return fullUrl;
    });
    const showEditForm = ref(false);
    const showReplies = ref(false);
    const replyContent = ref("");
    const editContent = ref(props.comment.content);
    const submittingReply = ref(false);
    const submittingEdit = ref(false);

    const isUserOwnComment = computed(() => {
      return props.comment.author.username === authStore.user?.username;
    });

    const commentClasses = computed(() => {
      const baseClasses =
        "bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow";

      if (props.depth === 0) {
        return baseClasses + " mb-6";
      } else {
        return baseClasses + " ml-6 border-l-4 border-l-primary-200 bg-gray-50";
      }
    });

    const toggleReplyForm = () => {
      showReplyForm.value = !showReplyForm.value;
      if (!showReplyForm.value) {
        replyContent.value = "";
      }
    };

    const toggleEditForm = () => {
      showEditForm.value = !showEditForm.value;
      if (!showEditForm.value) {
        editContent.value = props.comment.content; // Reset to original content
      }
    };

    const toggleReplies = () => {
      showReplies.value = !showReplies.value;
    };

    const submitEdit = async () => {
      if (!editContent.value.trim()) return;

      submittingEdit.value = true;
      try {
        // Emit event to parent
        emit("edit", {
          commentId: props.comment.id,
          content: editContent.value.trim(),
        });

        // Close edit form
        showEditForm.value = false;
      } catch (error) {
        console.error("Erro ao editar comentário:", error);
      } finally {
        submittingEdit.value = false;
      }
    };

    const submitReply = async () => {
      if (!replyContent.value.trim()) return;

      submittingReply.value = true;
      try {
        // Emit event to parent
        emit("reply", {
          content: replyContent.value.trim(),
          parentCommentId: props.comment.id,
        });

        // Reset form
        replyContent.value = "";
        showReplyForm.value = false;
      } catch (error) {
        console.error("Erro ao enviar resposta:", error);
      } finally {
        submittingReply.value = false;
      }
    };

    const handleLike = () => {
      if (isUserOwnComment.value) return;
      emit("like", {
        comment: props.comment,
        postId: props.postId,
      });
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    // Watch for changes in comment content to sync editContent
    watch(
      () => props.comment.content,
      (newContent, oldContent) => {
        console.log("📝 CommentThread viu mudança no content:", {
          commentId: props.comment.id,
          depth: props.depth,
          newContent: newContent?.substring(0, 50) + "...",
          oldContent: oldContent?.substring(0, 50) + "...",
          isDifferent: newContent !== oldContent,
        });

        // Forcar atualização imediata quando conteúdo muda
        if (newContent !== oldContent) {
          console.log("🔄 Atualizando editContent devido a mudança externa");
          editContent.value = newContent;
        }
      },
      { immediate: true }
    );

    // Watch for changes in the entire comment object
    watch(
      () => props.comment,
      (newComment, oldComment) => {
        console.log("🔄 CommentThread recebeu novo comment:", {
          id: newComment?.id,
          content: newComment?.content?.substring(0, 50) + "...",
          changed: newComment?.content !== oldComment?.content,
        });

        // Sempre sincronizar editContent com o conteúdo atual do comentário
        if (newComment && newComment.content !== editContent.value) {
          editContent.value = newComment.content;
        }
      },
      { deep: true, immediate: true }
    );

    return {
      authStore,
      commentAuthorImageUrl,
      showReplyForm,
      showEditForm,
      showReplies,
      replyContent,
      editContent,
      submittingReply,
      submittingEdit,
      isUserOwnComment,
      commentClasses,
      toggleReplyForm,
      toggleEditForm,
      toggleReplies,
      submitEdit,
      submitReply,
      handleLike,
      formatDate,
    };
  },
};
</script>

<style scoped>
/* Additional styles for comment threads */
.comment-thread-nested {
  position: relative;
}

.comment-thread-nested::before {
  content: "";
  position: absolute;
  left: -16px;
  top: 40px;
  bottom: 20px;
  width: 2px;
  background: linear-gradient(to bottom, #e5e7eb, transparent);
  border-radius: 1px;
}

/* Smooth animations */
.comment-transition-enter-active,
.comment-transition-leave-active {
  transition: all 0.3s ease;
}

.comment-transition-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.comment-transition-leave-to {
  opacity: 0;
  transform: scaleY(0.8);
}
</style>
